/**
 * useScheduleData.js — Mesai planı veri yönetimi hook'u
 */
import { useState, useEffect, useCallback } from 'react';
import api, { workScheduleApi } from '../../../api/axios';
import toast from 'react-hot-toast';

export default function useScheduleData({ language = 'tr' } = {}) {
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentAssignments, setDepartmentAssignments] = useState({});
  const [employees, setEmployees] = useState([]);
  const [employeeOverrides, setEmployeeOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /** Mesai planı şablonlarını çek */
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await workScheduleApi.getTemplates(false);
      setTemplates(res.data || []);
    } catch {
      toast.error('Mesai planları yüklenemedi.');
    }
  }, []);

  /** Departman + atamaları çek */
  const fetchDepartments = useCallback(async () => {
    try {
      const [deptRes, assignRes] = await Promise.all([
        api.get('/employee/department/list'),
        workScheduleApi.getDepartmentAssignments(),
      ]);
      const deptList = deptRes.data || [];
      const assignments = assignRes.data || [];

      const assignMap = {};
      assignments.forEach((item) => {
        assignMap[item.department_id] = {
          work_schedule_id: item.work_schedule_id ? String(item.work_schedule_id) : '',
          work_schedule_name: item.work_schedule_name || '',
          employee_count: item.employee_count || 0,
        };
      });

      const normalized = deptList.map((d) => ({
        ...d,
        employee_count: assignMap[d.id]?.employee_count || 0,
      }));

      const selections = {};
      normalized.forEach((d) => {
        selections[d.id] = assignMap[d.id]?.work_schedule_id || '';
      });

      setDepartments(normalized);
      setDepartmentAssignments(selections);
    } catch {
      toast.error('Departman atamaları yüklenemedi.');
    }
  }, []);

  /** Personel + istisnaları çek */
  const fetchOverrides = useCallback(async () => {
    try {
      const [empRes, ovrRes] = await Promise.all([
        api.get('/employee/list?status=ACTIVE'),
        workScheduleApi.getEmployeeOverrides(true),
      ]);
      setEmployees(empRes.data || []);
      setEmployeeOverrides(ovrRes.data || []);
    } catch {
      toast.error('Personel istisnaları alınamadı.');
    }
  }, []);

  /** Tüm verileri çek */
  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchTemplates(), fetchDepartments(), fetchOverrides()]);
    setLoading(false);
  }, [fetchTemplates, fetchDepartments, fetchOverrides]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /** Departman atamalarını kaydet */
  const saveDepartmentAssignments = useCallback(async (assignments) => {
    setSaving(true);
    try {
      const payload = Object.entries(assignments)
        .filter(([, scheduleId]) => scheduleId)
        .map(([deptId, scheduleId]) => ({
          department_id: parseInt(deptId, 10),
          work_schedule_id: parseInt(scheduleId, 10),
        }));
      await workScheduleApi.assignDepartments(payload);
      toast.success('Departman atamaları kaydedildi.');
      setDepartmentAssignments(assignments);
    } catch {
      toast.error('Departman atamaları kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  }, []);

  /** Mesai planı şablonu sil */
  const deleteTemplate = useCallback(async (id) => {
    try {
      await workScheduleApi.deleteTemplate(id);
      toast.success('Mesai planı silindi.');
      await fetchTemplates();
    } catch {
      toast.error('Mesai planı silinemedi.');
    }
  }, [fetchTemplates]);

  return {
    templates,
    departments,
    departmentAssignments,
    employees,
    employeeOverrides,
    loading,
    saving,
    refresh: loadAll,
    fetchTemplates,
    saveDepartmentAssignments,
    deleteTemplate,
    setDepartmentAssignments,
  };
}